import { AxiosHeaders, isAxiosError, isCancel } from 'axios';
import { ApiErrorKind, HTTP_HEADERS } from './constants';
import type { ApiResponse, ProblemDetails } from './types';

export interface ApiErrorInit {
  kind: ApiErrorKind;
  message: string;
  status?: number;
  /** Field name → messages, for `validation` errors. */
  details?: Record<string, string[]>;
  /** The raw response body, for debugging. Don't render it. */
  body?: unknown;
  requestId?: string;
  cause?: unknown;
}

/**
 * The only error type the API layer throws. Every axios, envelope and auth failure is mapped to one
 * of these, so callers branch on `kind` instead of digging through `error.response.data.message`.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly details: Record<string, string[]> | undefined;
  readonly body: unknown;
  readonly requestId: string | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.details = init.details;
    this.body = init.body;
    this.requestId = init.requestId;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** True for the CMPS envelope `{ success, message?, data? }`. */
export const isApiResponse = (value: unknown): value is ApiResponse<unknown> =>
  isRecord(value) && typeof value.success === 'boolean' && ('data' in value || 'message' in value);

const isProblemDetails = (value: unknown): value is ProblemDetails =>
  isRecord(value) && ('title' in value || 'errors' in value || 'detail' in value);

const kindForStatus = (status: number): ApiErrorKind => {
  if (status === 400 || status === 422) return ApiErrorKind.Validation;
  if (status === 401) return ApiErrorKind.Unauthorized;
  if (status === 403) return ApiErrorKind.Forbidden;
  if (status === 404) return ApiErrorKind.NotFound;
  if (status === 409) return ApiErrorKind.Conflict;
  if (status >= 500) return ApiErrorKind.Server;
  return ApiErrorKind.Http;
};

/** Best human-readable message in a CMPS error body (envelope or problem details). */
const messageFromBody = (body: unknown): string | undefined => {
  if (isApiResponse(body) && body.message) return body.message;
  if (isProblemDetails(body)) return body.detail ?? body.title;
  if (typeof body === 'string' && body.trim()) return body;
  return undefined;
};

const requestIdOf = (headers: unknown): string | undefined => {
  const value: unknown =
    headers instanceof AxiosHeaders
      ? headers.get(HTTP_HEADERS.requestId)
      : isRecord(headers)
        ? headers[HTTP_HEADERS.requestId]
        : undefined;
  return typeof value === 'string' ? value : undefined;
};

/** Normalises anything thrown during a request into an `ApiError`. Idempotent. */
export const toApiError = (error: unknown): ApiError => {
  if (isApiError(error)) return error;

  if (isCancel(error)) {
    return new ApiError({ kind: ApiErrorKind.Canceled, message: 'Request canceled', cause: error });
  }

  if (isAxiosError(error)) {
    const requestId = requestIdOf(error.config?.headers);
    const { response } = error;

    if (!response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      return new ApiError({
        kind: timedOut ? ApiErrorKind.Timeout : ApiErrorKind.Network,
        message: timedOut ? 'The request timed out' : 'Network error',
        requestId,
        cause: error,
      });
    }

    const body: unknown = response.data;
    return new ApiError({
      kind: kindForStatus(response.status),
      status: response.status,
      message: messageFromBody(body) ?? error.message,
      details: isProblemDetails(body) ? body.errors : undefined,
      body,
      requestId,
      cause: error,
    });
  }

  return new ApiError({
    kind: ApiErrorKind.Unknown,
    message: error instanceof Error ? error.message : 'Unexpected error',
    cause: error,
  });
};
